/**
 * React 19 compatible wrapper for react-ga using react-ga4
 * Maintains exact same API and behavior as react-ga
 */

import ReactGA4 from 'react-ga4';

// Store tracking ID and options for later use
let currentTrackingId = null;
let currentOptions = {};

// Store transaction and items for ecommerce (same behavior as react-ga)
let pendingTransaction = null;
let pendingItems = [];

// Initialize - matches react-ga behavior exactly
const initialize = (trackingId, options = {}) => {
    if (typeof window === 'undefined') return;
    
    currentTrackingId = trackingId;
    currentOptions = options || {};
    
    // For Universal Analytics (UA-*), load gtag script manually (same as react-ga)
    if (trackingId.startsWith('UA-')) {
        // Load gtag script for Universal Analytics
        if (!window.gtag) {
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
            document.head.appendChild(script);
            
            window.dataLayer = window.dataLayer || [];
            window.gtag = function() {
                window.dataLayer.push(arguments);
            };
            window.gtag('js', new Date());
        }
        
        // Configure with options (same as react-ga)
        window.gtag('config', trackingId, options.gaOptions || {});
    } else {
        // GA4 format - use react-ga4's initialize
        ReactGA4.initialize(trackingId, options);
    }
};

// Pageview tracking - matches react-ga behavior
const pageview = (path, options = {}) => {
    if (typeof window === 'undefined') return;
    
    if (currentTrackingId && currentTrackingId.startsWith('UA-')) {
        // Universal Analytics - use gtag directly (same as react-ga)
        window.gtag('config', currentTrackingId, {
            page_path: path,
            ...options
        });
    } else {
        // GA4 - use react-ga4
        ReactGA4.send({ hitType: 'pageview', page: path, ...options });
    }
};

// Event tracking - matches react-ga behavior
const event = (args) => {
    if (typeof window === 'undefined') return;
    
    if (currentTrackingId && currentTrackingId.startsWith('UA-')) {
        // Universal Analytics - use gtag directly (same as react-ga)
        window.gtag('event', args.action || args.eventCategory, {
            event_category: args.category || args.eventCategory,
            event_label: args.label || args.eventLabel,
            value: args.value,
            ...args
        });
    } else {
        // GA4 - use react-ga4
        ReactGA4.event({
            action: args.action || args.eventCategory,
            category: args.category || args.eventCategory,
            label: args.label || args.eventLabel,
            value: args.value,
            ...args
        });
    }
};

// Legacy API compatibility - matches react-ga behavior exactly
const ga = (...args) => {
    if (typeof window === 'undefined') return;
    
    // Ensure gtag is available for UA
    if (currentTrackingId && currentTrackingId.startsWith('UA-') && !window.gtag) {
        // Initialize if not already done
        initialize(currentTrackingId, currentOptions);
    }
    
    // Handle Universal Analytics format: ReactGA.ga('send', {hitType: 'pageview', ...})
    if (args[0] === 'send' && args[1] && args[1].hitType) {
        const hitType = args[1].hitType;
        if (hitType === 'pageview') {
            pageview(args[1].page || window.location.pathname);
        } else if (hitType === 'event') {
            event({
                action: args[1].eventAction,
                category: args[1].eventCategory,
                label: args[1].eventLabel,
                value: args[1].eventValue
            });
        }
    } else if (args[0] === 'require') {
        // Handle ReactGA.ga('require', 'linker') or ReactGA.ga('require', 'ecommerce')
        if (args[1] === 'linker') {
            // Configure linker (same as react-ga)
            window.gtag('config', currentTrackingId, {
                linker: { domains: [] }
            });
        } else if (args[1] === 'ecommerce') {
            // Ecommerce plugin requirement (same as react-ga - no-op, handled by plugin.execute)
        }
    } else if (args[0] && typeof args[0] === 'string' && args[0].indexOf(':') > 0) {
        // Handle ReactGA.ga('linker:autoLink', [...]) or ReactGA.ga('ecommerce:send')
        const [command, subcommand] = args[0].split(':');
        if (command === 'linker' && subcommand === 'autoLink') {
            // Configure auto-linker (same as react-ga)
            window.gtag('config', currentTrackingId, {
                linker: { domains: args[1] || [] }
            });
        } else if (command === 'ecommerce' && subcommand === 'send') {
            // Ecommerce send - trigger purchase event (same behavior as react-ga)
            if (pendingTransaction) {
                window.gtag('event', 'purchase', {
                    ...pendingTransaction,
                    items: pendingItems
                });
                // Reset for next transaction (same as react-ga)
                pendingTransaction = null;
                pendingItems = [];
            }
        }
    } else {
        // Direct gtag/ga call (same as react-ga)
        if (currentTrackingId && currentTrackingId.startsWith('UA-')) {
            window.gtag(...args);
        } else {
            ReactGA4.ga(...args);
        }
    }
};

// Plugin system compatibility (for ecommerce) - matches react-ga behavior exactly
const plugin = {
    execute: (pluginName, method, ...args) => {
        if (typeof window === 'undefined') return;
        
        // Ensure gtag is available
        if (currentTrackingId && currentTrackingId.startsWith('UA-') && !window.gtag) {
            initialize(currentTrackingId, currentOptions);
        }
        
        if (pluginName === 'ecommerce') {
            if (method === 'addTransaction') {
                // Store transaction data (same as react-ga)
                const transaction = args[0];
                pendingTransaction = {
                    transaction_id: transaction.id,
                    affiliation: transaction.affiliation,
                    value: transaction.revenue || transaction.total,
                    currency: transaction.currency || 'USD',
                    tax: transaction.tax,
                    shipping: transaction.shipping
                };
            } else if (method === 'addItem') {
                // Add item to pending items (same as react-ga)
                const item = args[0];
                pendingItems.push({
                    item_id: item.id || item.sku,
                    item_name: item.name,
                    item_category: item.category,
                    quantity: parseInt(item.quantity) || 1,
                    price: parseFloat(item.price)
                });
            } else if (method === 'send') {
                // Send purchase event (same behavior as react-ga)
                if (pendingTransaction) {
                    window.gtag('event', 'purchase', {
                        ...pendingTransaction,
                        items: pendingItems
                    });
                    // Reset for next transaction (same as react-ga)
                    pendingTransaction = null;
                    pendingItems = [];
                }
            }
        }
    }
};

// Set function - matches react-ga behavior
const set = (fieldsObject) => {
    if (typeof window === 'undefined') return;
    
    if (currentTrackingId && currentTrackingId.startsWith('UA-')) {
        // Universal Analytics - use gtag set (same as react-ga)
        if (!window.gtag) {
            initialize(currentTrackingId, currentOptions);
        }
        window.gtag('set', fieldsObject);
    } else {
        // GA4 - use react-ga4
        ReactGA4.set(fieldsObject);
    }
};

const ReactGA = {
    initialize,
    pageview,
    event,
    ga,
    plugin,
    set
};

export default ReactGA;
export { set };

